'use client';

import Link from 'next/link';
import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Logo from '@/components/Logo';
import { csrfHeaders } from '@/lib/csrf';

const CONDICIONES_IVA = [
  { value: 'Monotributista',        label: 'Monotributista' },
  { value: 'Responsable Inscripto', label: 'Responsable Inscripto' },
  { value: 'Exento',                label: 'Exento' },
];

export default function RegisterPage() {
  const router = useRouter();

  // Datos de usuario
  const [nombre,   setNombre]   = useState('');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');

  // Datos de empresa
  const [cuit,         setCuit]         = useState('');
  const [razonSocial,  setRazonSocial]  = useState('');
  const [condicionIva, setCondicionIva] = useState('Monotributista');
  const [domicilio,    setDomicilio]    = useState('');

  const [aceptoTerminos, setAceptoTerminos] = useState(false);

  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState<string | null>(null);
  const [paso,            setPaso]            = useState<1 | 2>(1);
  const [ivaOpen,         setIvaOpen]         = useState(false);
  const ivaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ivaRef.current && !ivaRef.current.contains(e.target as Node)) setIvaOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function formatCuit(v: string) {
    const d = v.replace(/\D/g, '').slice(0, 11);
    if (d.length <= 2) return d;
    if (d.length <= 10) return `${d.slice(0, 2)}-${d.slice(2)}`;
    return `${d.slice(0, 2)}-${d.slice(2, 10)}-${d.slice(10)}`;
  }

  function paso1Valido() {
    return nombre.trim() && email.trim() && password.length >= 8 && password === password2;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (paso === 1) { if (paso1Valido()) setPaso(2); return; }

    setError(null);
    setLoading(true);
    try {
      const res  = await fetch('/api/auth/register', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body:    JSON.stringify({
          nombre, email, password,
          cuit:           cuit.replace(/\D/g, ''),
          razonSocial:    razonSocial.trim(),
          condicionIva,
          domicilio:      domicilio.trim(),
          aceptoTerminos: true,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error ?? 'Error al registrarse'); return; }
      router.push('/dashboard');
      router.refresh();
    } catch {
      setError('No se pudo conectar con el servidor');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 py-12">
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-96 h-96 bg-mrcode-gold/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative w-full max-w-sm">

        {/* Logo */}
        <div className="flex flex-col items-center mb-8 gap-2">
          <Logo height={60} href="/" className="brightness-[1.8]" />
          <p className="text-xs text-white/25">Facturación Electrónica · ARCA</p>
        </div>

        {/* Pasos */}
        <div className="flex items-center gap-2 mb-6 px-1">
          {[1, 2].map(n => (
            <div key={n} className="flex items-center gap-2 flex-1">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${paso >= n ? 'bg-mrcode-gold text-black' : 'bg-white/8 text-white/30'}`}>
                {n}
              </div>
              <span className={`text-xs transition-colors ${paso >= n ? 'text-white/60' : 'text-white/25'}`}>
                {n === 1 ? 'Tu cuenta' : 'Tu empresa'}
              </span>
              {n < 2 && <div className={`flex-1 h-px ${paso > n ? 'bg-mrcode-gold/40' : 'bg-white/8'}`} />}
            </div>
          ))}
        </div>

        {/* Card */}
        <div className="bg-mrcode-surface border border-white/8 rounded-2xl p-8">

          {error && (
            <div className="mb-5 flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-red-400 text-sm">
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">

            {paso === 1 ? (
              <>
                <h1 className="text-xl font-bold text-white mb-1">Crear cuenta</h1>
                <p className="text-sm text-white/40 mb-5">Tus datos de acceso</p>

                <div>
                  <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">Nombre completo</label>
                  <input
                    type="text" value={nombre} onChange={e => setNombre(e.target.value)}
                    required autoFocus placeholder="Juan García"
                    className="w-full bg-white/6 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-white/25 focus:outline-none focus:border-mrcode-gold/60 transition-colors text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">Email</label>
                  <input
                    type="email" value={email} onChange={e => setEmail(e.target.value)}
                    required placeholder="tu@empresa.com"
                    className="w-full bg-white/6 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-white/25 focus:outline-none focus:border-mrcode-gold/60 transition-colors text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">Contraseña</label>
                  <input
                    type="password" value={password} onChange={e => setPassword(e.target.value)}
                    required placeholder="Mínimo 8 caracteres"
                    className="w-full bg-white/6 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-white/25 focus:outline-none focus:border-mrcode-gold/60 transition-colors text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">Repetir contraseña</label>
                  <input
                    type="password" value={password2} onChange={e => setPassword2(e.target.value)}
                    required placeholder="••••••••"
                    className={`w-full bg-white/6 border rounded-lg px-4 py-2.5 text-white placeholder-white/25 focus:outline-none transition-colors text-sm ${password2 && password !== password2 ? 'border-red-500/50 focus:border-red-500' : 'border-white/10 focus:border-mrcode-gold/60'}`}
                  />
                  {password2 && password !== password2 && (
                    <p className="text-xs text-red-400 mt-1">Las contraseñas no coinciden</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={!paso1Valido()}
                  className="w-full mt-2 py-2.5 rounded-lg bg-mrcode-gold text-black font-bold text-sm hover:bg-mrcode-gold/90 transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
                >
                  Continuar
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              </>
            ) : (
              <>
                <h1 className="text-xl font-bold text-white mb-1">Tu empresa</h1>
                <p className="text-sm text-white/40 mb-5">Datos para emitir facturas AFIP</p>

                <div>
                  <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">CUIT</label>
                  <input
                    type="text" value={cuit}
                    onChange={e => setCuit(formatCuit(e.target.value))}
                    required autoFocus placeholder="20-12345678-9"
                    className="w-full bg-white/6 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-white/25 focus:outline-none focus:border-mrcode-gold/60 transition-colors text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">Razón Social</label>
                  <input
                    type="text" value={razonSocial} onChange={e => setRazonSocial(e.target.value)}
                    required placeholder="Nombre o razón social tal como figura en AFIP"
                    className="w-full bg-white/6 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-white/25 focus:outline-none focus:border-mrcode-gold/60 transition-colors text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">Condición IVA</label>
                  <div ref={ivaRef} className="relative">
                    <button
                      type="button"
                      onClick={() => setIvaOpen(o => !o)}
                      className="w-full bg-white/6 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm text-left flex items-center justify-between hover:border-white/20 focus:outline-none focus:border-mrcode-gold/60 transition-colors"
                    >
                      <span>{CONDICIONES_IVA.find(c => c.value === condicionIva)?.label ?? condicionIva}</span>
                      <svg className={`w-4 h-4 text-white/40 transition-transform shrink-0 ${ivaOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {ivaOpen && (
                      <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-[#1a1a1a] border border-white/15 rounded-xl shadow-2xl overflow-hidden">
                        {CONDICIONES_IVA.map(c => (
                          <button
                            key={c.value}
                            type="button"
                            onClick={() => { setCondicionIva(c.value); setIvaOpen(false); }}
                            className={`w-full px-4 py-2.5 text-sm text-left transition-colors ${
                              condicionIva === c.value
                                ? 'bg-mrcode-gold/15 text-mrcode-gold font-semibold'
                                : 'text-white/70 hover:bg-white/6 hover:text-white'
                            }`}
                          >
                            {c.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">Domicilio fiscal <span className="text-white/25 normal-case font-normal">(opcional)</span></label>
                  <input
                    type="text" value={domicilio} onChange={e => setDomicilio(e.target.value)}
                    placeholder="Av. Corrientes 1234, CABA"
                    className="w-full bg-white/6 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-white/25 focus:outline-none focus:border-mrcode-gold/60 transition-colors text-sm"
                  />
                </div>

                {/* Términos y Condiciones */}
                <label className="flex items-start gap-3 cursor-pointer group mt-1">
                  <div className="relative shrink-0 mt-0.5">
                    <input
                      type="checkbox"
                      checked={aceptoTerminos}
                      onChange={e => setAceptoTerminos(e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${aceptoTerminos ? 'bg-mrcode-gold border-mrcode-gold' : 'border-white/20 bg-white/5 group-hover:border-white/40'}`}>
                      {aceptoTerminos && (
                        <svg className="w-2.5 h-2.5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-white/50 leading-relaxed">
                    Leí y acepto los{' '}
                    <a href="/terminos" target="_blank" rel="noopener noreferrer" className="text-mrcode-gold hover:underline" onClick={e => e.stopPropagation()}>
                      Términos y Condiciones
                    </a>
                    {' '}y la{' '}
                    <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-mrcode-gold hover:underline" onClick={e => e.stopPropagation()}>
                      Política de Privacidad
                    </a>
                    {' '}del servicio
                  </span>
                </label>

                <div className="flex gap-3 mt-2">
                  <button
                    type="button"
                    onClick={() => { setPaso(1); setError(null); }}
                    className="px-4 py-2.5 rounded-lg border border-white/10 text-white/50 hover:bg-white/5 transition-colors text-sm font-medium"
                  >
                    Atrás
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !cuit || !razonSocial || !aceptoTerminos}
                    className="flex-1 py-2.5 rounded-lg bg-mrcode-gold text-black font-bold text-sm hover:bg-mrcode-gold/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading && (
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                      </svg>
                    )}
                    {loading ? 'Creando cuenta...' : 'Crear cuenta'}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>

        <p className="text-center text-sm text-white/30 mt-6">
          ¿Ya tenés cuenta?{' '}
          <Link href="/login" className="text-mrcode-gold hover:underline font-medium">
            Iniciar sesión
          </Link>
        </p>
      </div>
    </div>
  );
}
